import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { MessageService } from "primeng/api";
import { forkJoin, of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { AutomationActions } from "../actions/automation.actions";

@Injectable()
export class AutomationEffects {
  constructor(private actions$: Actions, private restService: RestService, private toaster: ToasterService) { }
  private messageService = inject(MessageService);

  // Applications
  loadApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadApplications),
      mergeMap(({ projectId, automationProjectId }) =>
        this.restService
          .getAutomationApplications(projectId, automationProjectId)
          .pipe(
            map((applications) =>
              AutomationActions.loadApplicationsSuccess({
                automationProjectId: automationProjectId,
                applications: applications?.data,
              })
            ),
            catchError((error) =>
              of(AutomationActions.automationFailure({ error: error.message }))
            )
          )
      )
    )
  );

  addApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addApplication),
      mergeMap(({ projectId, automationProjectId, application }) =>
        this.restService
          .addAutomationApplications(
            projectId,
            automationProjectId,
            application
          )
          .pipe(
            map((app) =>
              AutomationActions.addApplicationSuccess({
                automationProjectId,
                application: {
                  ...app?.data
                },
              }),
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_APPLICATION_CREATION_SUCCESS })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteApplication),
      mergeMap(({ projectId, automationProjectId, id }) =>
        this.restService
          .deleteAutomationApplicationsById(projectId, automationProjectId, id)
          .pipe(
            map(() =>
              AutomationActions.deleteApplicationSuccess({
                automationProjectId,
                id,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteApplications),
      mergeMap(({ projectId, automationProjectId, idList }) =>
        this.restService
          .deleteAutomationApplications(projectId, automationProjectId, idList)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_APPLICATION_DELETION_SUCCESS });
              return AutomationActions.deleteApplicationsSuccess({
                automationProjectId,
                idList,
              })
            }

            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_APPLICATION_DELETION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateApplication),
      mergeMap(({ projectId, automationProjectId, id, changes }) =>
        this.restService
          .updateAutomationApplicationsById(
            projectId,
            automationProjectId,
            id,
            changes
          )
          .pipe(
            map((application) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_APPLICATION_UPDATE_SUCCESS });
              return AutomationActions.updateApplicationSuccess({
                automationProjectId,
                application: application?.data,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_APPLICATION_UPDATE_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  // Test Suits

  addTestSuits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addScriptGroup),
      mergeMap(({ projectId, automationProjectId, scriptGroup }) =>
        this.restService
          .addAutomationTestSuits(projectId, automationProjectId, scriptGroup)
          .pipe(
            map((response) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.TEST_SUITE_CREATION_SUCCESS });
              return AutomationActions.addScriptGroupSuccess({
                scriptGroup: response?.data,
              });
            }),
            catchError((error) => {
              if (error?.message) {
                this.toaster.pop({ type: 'error', text: error.message });
              }
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteScriptGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteScriptGroup),
      mergeMap(({ projectId, automationProjectId, id }) =>
        this.restService
          .deleteAutomationTestSuitsById(projectId, automationProjectId, id)
          .pipe(
            map(() =>
              AutomationActions.deleteScriptGroupSuccess({
                id,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteScriptGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteScriptGroups),
      mergeMap(({ projectId, automationProjectId, idList }) =>
        this.restService
          .bulkDeleteAutomationTestSuites(projectId, automationProjectId, idList)
          .pipe(
            map((response: any) => {
              const skippedSuiteIds: any[] = response?.data || [];
              const deletableSuiteIds = idList.filter((id: any) => !skippedSuiteIds.includes(id));
              if (skippedSuiteIds.length > 0) {
                this.toaster.pop({
                  type: 'error',
                  text: MessageEnum.AUTOMATION_TEST_SUITE_DELETION_FAILURE_HAS_SCRIPTS,
                });
              } else {
                this.toaster.pop({
                  type: 'success',
                  text: MessageEnum.TEST_SUITE_DELETION_SUCCESS,
                });
              }

              return AutomationActions.deleteScriptGroupsSuccess({ idList: deletableSuiteIds });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateTestSuits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateScriptGroup),
      mergeMap(({ projectId, automationProjectId, id, changes }) =>
        this.restService
          .updateAutomationTestSuitsById(
            projectId,
            automationProjectId,
            id,
            changes
          )
          .pipe(
            map((scriptGroup) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_TEST_SUITE_UPDATE_SUCCESS });
              return AutomationActions.updateScriptGroupSuccess({
                scriptGroup,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_TEST_SUITE_UPDATE_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  // Test Script

  addTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addTestScript),
      mergeMap(({ projectId, automationProjectId, testScript }) =>
        this.restService
          .addAutomationTestScript(projectId, automationProjectId, testScript)
          .pipe(
            map((response) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_TEST_SCRIPT_CREATE_SUCCESS });
              return AutomationActions.addTestScriptSuccess({
                testScript: response?.data,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteTestScript),
      mergeMap(({ projectId, automationProjectId, id }) =>
        this.restService
          .deleteAutomationTestScriptById(projectId, automationProjectId, id)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_TEST_SCRIPT_DELETE_SUCCESS });
              return AutomationActions.deleteTestScriptSuccess({
                id,
              });
            }
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteTestScripts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteTestScripts),
      mergeMap(({ projectId, automationProjectId, idList }) =>
        this.restService
          .bulkDeleteAutomationTestScripts(projectId, automationProjectId, idList)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_TEST_SCRIPT_DELETE_SUCCESS });
              return AutomationActions.deleteTestScriptsSuccess({
                idList,
              });
            }
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );


  updateTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateTestScript),
      mergeMap(({ projectId, automationProjectId, id, changes }) =>
        this.restService
          .updateAutomationTestScriptById(
            projectId,
            automationProjectId,
            id,
            changes
          )
          .pipe(
            map((testScript) =>
              AutomationActions.updateTestScriptSuccess({
                testScript,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  renameTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.renameTestScript),
      mergeMap(({ projectId, automationProjectId, id, changes }) =>
        this.restService
          .renameScript(projectId, automationProjectId, id, changes)
          .pipe(
            map((testScript) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_TEST_SCRIPT_UPDATE_SUCCESS });
              return AutomationActions.updateTestScriptSuccess({
                testScript,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_TEST_SCRIPT_UPDATE_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  mapToTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.mapToTestScript),
      mergeMap(({ projectId, automationProjectId, id, mappingId }) =>
        this.restService
          .mapToTestCase(projectId, automationProjectId, id, mappingId)
          .pipe(
            map((testScript) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.TEST_CASE_MAP_SUCCESS });
              return AutomationActions.updateTestScriptSuccess({
                testScript: testScript?.data,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.TEST_CASE_MAP_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  // Both Test Suit and test case
  loadTestSuitsAndTestCases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadScriptGroup),
      mergeMap(({ projectId, automationProjectId }) =>
        forkJoin({
          scriptGroups: this.restService.getAutomationTestSuits(
            projectId,
            automationProjectId
          ),
          testScripts: this.restService.getAutomationTestScripts(
            projectId,
            automationProjectId
          ),
        }).pipe(
          map(({ scriptGroups, testScripts }) => {
            const testSuite = scriptGroups?.data?.filter(
              (item: any) => item?.type === "SCRIPT"
            );
            const testScript = testScripts?.data?.filter(
              (item: any) => item?.type === "SCRIPT"
            );

            return AutomationActions.loadScriptGroupSuccess({
              scriptGroup: testSuite,
              testScript: testScript,
            });
          }),
          catchError((error) =>
            of(AutomationActions.automationFailure({ error: error.message }))
          )
        )
      )
    )
  );

  // Reusable Component

  addReusableComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addReusableComponent),
      mergeMap(({ projectId, automationProjectId, reusableComponent }) =>
        this.restService
          .addAutomationTestSuits(
            projectId,
            automationProjectId,
            reusableComponent
          )
          .pipe(
            map((response) =>
              AutomationActions.addReusableComponentSuccess({
                reusableComponent: response?.data,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteReusableComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteReusableComponent),
      mergeMap(({ projectId, automationProjectId, id }) =>
        this.restService
          .deleteAutomationTestSuitsById(projectId, automationProjectId, id)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.REUSABLE_COMPONENT_DELETION_SUCCESS });
              return AutomationActions.deleteReusableComponentSuccess({
                id,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_COMPONENT_DELETION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteReusableComponents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteReusableComponents),
      mergeMap(({ projectId, automationProjectId, idList }) =>
        this.restService
          .bulkDeleteAutomationTestSuites(projectId, automationProjectId, idList)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.REUSABLE_COMPONENT_DELETION_SUCCESS });
              return AutomationActions.deleteReusableComponentsSuccess({
                idList,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_COMPONENT_DELETION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateReusableComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateReusableComponent),
      mergeMap(({ projectId, automationProjectId, id, changes }) =>
        this.restService
          .updateAutomationTestSuitsById(
            projectId,
            automationProjectId,
            id,
            changes
          )
          .pipe(
            map((reusableComponent) =>
              AutomationActions.updateReusableComponentSuccess({
                reusableComponent,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  // Reusable Test Script

  addReusableTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addReusableScript),
      mergeMap(({ projectId, automationProjectId, reusableScript }) =>
        this.restService
          .addAutomationTestScript(
            projectId,
            automationProjectId,
            reusableScript
          )
          .pipe(
            map((response) =>
              AutomationActions.addReusableScriptSuccess({
                reusableScript: response?.data,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteReusableTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteReusableScript),
      mergeMap(({ projectId, automationProjectId, id }) =>
        this.restService
          .deleteAutomationTestScriptById(projectId, automationProjectId, id)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.REUSABLE_SCRIPT_DELETION_SUCCESS });
              return AutomationActions.deleteReusableScriptSuccess({
                id,
              });
            }
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteReusableTestScripts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteReusableScripts),
      mergeMap(({ projectId, automationProjectId, idList }) =>
        this.restService
          .bulkDeleteAutomationTestScripts(projectId, automationProjectId, idList)
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.REUSABLE_SCRIPT_DELETION_SUCCESS });
              return AutomationActions.deleteReusableScriptsSuccess({
                idList,
              });
            }
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateReusableTestScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateReusableScript),
      mergeMap(({ projectId, automationProjectId, id, changes }) =>
        this.restService
          .renameScript(projectId, automationProjectId, id, changes)
          .pipe(
            map((reusableScript) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.REUSABLE_SCRIPT_UPDATE_SUCCESS });
              return AutomationActions.updateReusableScriptSuccess({
                reusableScript,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_SCRIPT_UPDATE_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  // Both reusable component and scripts
  loadReusableComponentsAndScripts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadReusableComponent),
      mergeMap(({ projectId, automationProjectId }) =>
        forkJoin({
          scriptGroups: this.restService.getAutomationTestSuits(
            projectId,
            automationProjectId
          ),
          testScripts: this.restService.getAutomationTestScripts(
            projectId,
            automationProjectId
          ),
        }).pipe(
          map(({ scriptGroups, testScripts }) => {
            const reusableComponents = scriptGroups?.data?.filter(
              (item: any) => item?.type === "REUSABLE_SCRIPT"
            );
            const reusableScripts = testScripts?.data?.filter(
              (item: any) => item?.type === "REUSABLE_SCRIPT"
            );

            return AutomationActions.loadReusableComponentSuccess({
              reusableComponent: reusableComponents,
              reusableScript: reusableScripts,
            });
          }),
          catchError((error) =>
            of(AutomationActions.automationFailure({ error: error.message }))
          )
        )
      )
    )
  );

  // Pages
  loadPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadPages),
      mergeMap(({ projectId, automationProjectId, applicationId }) =>
        this.restService
          .getAutomationPage(projectId, automationProjectId, applicationId)
          .pipe(
            map((pages) =>
              AutomationActions.loadPagesSuccess({
                applicationId,
                pages: pages?.data,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_PAGES_LOADING_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  addPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addPage),
      mergeMap(({ projectId, automationProjectId, applicationId, page }) =>
        this.restService
          .addAutomationPage(
            projectId,
            automationProjectId,
            applicationId,
            page
          )
          .pipe(
            map((app) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_PAGE_CREATION_SUCCESS });
              return AutomationActions.addPageSuccess({
                applicationId,
                page: {
                  id: app?.data?.id,
                  name: app?.data?.name,
                  url: app?.data?.url,
                },
              });
            }),
            catchError((error) => {
              let errorMessage = error?.message ? error?.message : MessageEnum.AUTOMATION_PAGE_CREATION_FAILURE;
              this.toaster.pop({ type: 'error', text: errorMessage });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deletePages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deletePage),
      mergeMap(({ projectId, automationProjectId, applicationId, id }) =>
        this.restService
          .deleteAutomationPageById(
            projectId,
            automationProjectId,
            applicationId,
            id
          )
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_PAGE_DELETION_SUCCESS });
              return AutomationActions.deletePageSuccess({ applicationId, id })
            }
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_PAGE_DELETION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deletePagesBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deletePages),
      mergeMap(({ projectId, automationProjectId, applicationId, idList }) =>
        this.restService
          .deleteAutomationPages(
            projectId,
            automationProjectId,
            applicationId,
            idList
          )
          .pipe(
            map(() => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_PAGE_DELETION_SUCCESS });
              return AutomationActions.deletePagesSuccess({ applicationId, idList });
            }

            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_PAGE_DELETION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updatePages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updatePage),
      mergeMap(
        ({ projectId, automationProjectId, applicationId, id, changes }) =>
          this.restService
            .updateAutomationPageById(
              projectId,
              automationProjectId,
              applicationId,
              id,
              changes
            )
            .pipe(
              map((application) => {
                this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_PAGE_UPDATE_SUCCESS });
                return AutomationActions.updatePageSuccess({
                  applicationId,
                  page: application?.data,
                });
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_PAGE_UPDATE_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error?.message })
                );
              })
            )
      )
    )
  );

  // UI Objects
  loadUiObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadUiObjects),
      mergeMap(({ projectId, automationProjectId, applicationId, pageId }) =>
        this.restService
          .getAutomationUiObject(
            projectId,
            automationProjectId,
            applicationId,
            pageId
          )
          .pipe(
            map((uiObjects) =>
              AutomationActions.loadUiObjectsSuccess({
                pageId: pageId,
                uiObjects: uiObjects?.data,
              })
            ),
            catchError((error) =>
              of(AutomationActions.automationFailure({ error: error.message }))
            )
          )
      )
    )
  );

  addUiObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addUiObject),
      mergeMap(
        ({ projectId, automationProjectId, applicationId, pageId, uiObject }) =>
          this.restService
            .addAutomationUiObject(
              projectId,
              automationProjectId,
              applicationId,
              pageId,
              uiObject
            )
            .pipe(
              map((app) =>
                AutomationActions.addUiObjectSuccess({
                  pageId,
                  uiObject: app?.data,
                }),
                this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_UI_OBJECT_CREATE_SUCCESS })
              ),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: error.message });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
      )
    )
  );

  deleteUiObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteUiObject),
      mergeMap(
        ({ projectId, automationProjectId, applicationId, pageId, id }) =>
          this.restService
            .deleteAutomationUiObjectById(
              projectId,
              automationProjectId,
              applicationId,
              pageId,
              id
            )
            .pipe(
              map(() => {
                this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_UI_OBJECT_DELETION_SUCCESS });
                return AutomationActions.deleteUiObjectSuccess({ pageId, id });
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_UI_OBJECT_DELETION_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
      )
    )
  );

  deleteUiObjectsBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteUiObjects),
      mergeMap(
        ({ projectId, automationProjectId, applicationId, pageId, idList }) =>
          this.restService
            .deleteAutomationUiObjects(
              projectId,
              automationProjectId,
              applicationId,
              pageId,
              idList
            )
            .pipe(
              map(() => {
                this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_UI_OBJECT_DELETION_SUCCESS });
                return AutomationActions.deleteUiObjectsSuccess({ pageId, idList });
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_UI_OBJECT_DELETION_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
      )
    )
  );

  updateUiObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateUiObject),
      mergeMap(
        ({
          projectId,
          automationProjectId,
          applicationId,
          pageId,
          id,
          changes,
          action
        }) =>
          this.restService
            .updateAutomationUiObjectById(
              projectId,
              automationProjectId,
              applicationId,
              pageId,
              id,
              changes,
            )
            .pipe(
              map((uiObject) => {
                console.log("action", action)
                if (action == "addLocator") {
                  this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_UI_LOCATOR_CREATE_SUCCESS });
                }
                else if (action == "edit") {
                  this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_UI_OBJECT_UPDATE_SUCCESS });
                }
                else if (action == "delete") {
                  this.toaster.pop({ type: 'success', text: MessageEnum.AUTOMATION_UI_OBJECT_LOCATOR_DELETION_SUCCESS });
                }

                return AutomationActions.updateUiObjectSuccess({
                  applicationId: applicationId,
                  pageId: pageId,
                  uiObject: uiObject?.data,
                });
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_UI_OBJECT_UPDATE_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
      )
    )
  );

  // Command Definitions
  loadCommandDefinitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadCommandDefinitions),
      mergeMap(() =>
        this.restService.getCommandDefinitions().pipe(
          map((commandDefinitions) =>
            AutomationActions.loadCommandDefinitionsSuccess({
              commandDefinitions: commandDefinitions?.data,
            })
          ),
          catchError((error) =>
            of(AutomationActions.automationFailure({ error: error.message }))
          )
        )
      )
    )
  );



  //service
    loadService$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AutomationActions.loadService),
        mergeMap(({ projectId, automationProjectId }) =>
          this.restService
            .getAllServices(projectId, automationProjectId)
            .pipe(
              map((services) =>
                AutomationActions.loadServiceSuccess({
                  apiServices: services?.data
                })
              ),
              catchError((error) =>
                of(AutomationActions.automationFailure({ error: error.message }))
              )
            )
        )
      )
    );
  
    addService$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AutomationActions.addService),
        mergeMap(({ projectId, automationProjectId, apiService }) =>
          this.restService
            .addRestAPIService(
              projectId,
              automationProjectId,
              apiService
            )
            .pipe(
              map((service) => {
                this.toaster.pop({ type: 'success', text: MessageEnum.CREATE_SERVICE_SUCCESS });
                return AutomationActions.addServiceSuccess({ service: service?.data});
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.CREATE_SERVICE_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
        )
      )
    );
  
    deleteServices$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AutomationActions.deleteServices),
        mergeMap(({ projectId, automationProjectId, idList }) =>
          this.restService
            .deleteServices(projectId, automationProjectId, idList)
            .pipe(
              map((response) =>
              {
                this.toaster.pop({ type: 'success', text: MessageEnum.DELETE_SERVICE_SUCCESS });
                return AutomationActions.deleteServicesSuccess({
                  idList: response?.data?.readyToDelete ? response?.data?.readyToDelete : idList
                });
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.DELETE_SERVICE_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
        )
      )
    );
  
    updateService$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AutomationActions.updateService),
        mergeMap(({ projectId, automationProjectId, id, changes }) =>
          this.restService
            .updateRestAPIService(
              projectId,
              automationProjectId,
              id,
              changes
            )
            .pipe(
              map((service) => {
                this.toaster.pop({ type: 'success', text: MessageEnum.UPDATE_SERVICE_SUCCESS });
                return AutomationActions.updateServiceSuccess({
                  service: service?.data
                });
              }),
              catchError((error) => {
                this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.UPDATE_SERVICE_FAILURE });
                return of(
                  AutomationActions.automationFailure({ error: error.message })
                );
              })
            )
        )
      )
    );


    
  //service operations
  loadServiceOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadServiceOperation),
      mergeMap(({ projectId, automationProjectId, serviceId }) =>
        this.restService
          .getAllServiceOperations(projectId, automationProjectId, serviceId)
          .pipe(
            map((services) =>
              AutomationActions.loadServiceOperationSuccess({
                serviceOperations: services?.data
              })
            ),
            catchError((error) =>
              of(AutomationActions.automationFailure({ error: error.message }))
            )
          )
      )
    )
  );

  addServiceOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addServiceOperation),
      mergeMap(({ projectId, automationProjectId, serviceId, serviceOperations }) =>
        this.restService
          .addServiceOperation(
            projectId,
            automationProjectId,
            serviceId,
            serviceOperations
          )
          .pipe(
            map((service) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.CREATE_SERVICE_OPERATION_SUCCESS });
              return AutomationActions.addServiceOperationSuccess({ serviceOperations: service?.data});
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.CREATE_SERVICE_OPERATION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteServicesOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteServiceOperations),
      mergeMap(({ projectId, automationProjectId, serviceId,idList }) =>
        this.restService
          .deleteServiceOperations(projectId, automationProjectId,serviceId, idList)
          .pipe(
            map((response) =>
            {
              this.toaster.pop({ type: 'success', text: MessageEnum.DELETE_SERVICE_OPERATION_SUCCESS });
              return AutomationActions.deleteServiceOperationsSuccess({
                idList: response?.data?.readyToDelete ? response?.data?.readyToDelete : idList
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.DELETE_SERVICE_OPERATION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateServiceOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateServiceOperation),
      mergeMap(({ projectId, automationProjectId, serviceId, id, changes }) =>
        this.restService
          .updateServiceOperation(
            projectId,
            automationProjectId,
            serviceId,
            id,
            changes
          )
          .pipe(
            map((service) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.UPDATE_SERVICE_OPERATION_SUCCESS });
              return AutomationActions.updateServiceOperationSuccess({
                serviceOperations: service?.data
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.UPDATE_SERVICE_OPERATION_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );


   //parameters
   loadOperationParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadOperationParameters),
      mergeMap(({ projectId, automationProjectId, serviceId, operationId }) =>
        this.restService
          .getParameters(projectId, automationProjectId, serviceId,operationId )
          .pipe(
            map((services) =>
              AutomationActions.loadOperationParametersSuccess({
                operationParameters: services?.data
              })
            ),
            catchError((error) =>
              of(AutomationActions.automationFailure({ error: error.message }))
            )
          )
      )
    )
  );

  addOperationParameter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addOperationParameter),
      mergeMap(({ projectId, automationProjectId, serviceId, operationId, operationParameters }) =>
        this.restService
          .addParameter(
            projectId,
            automationProjectId,
            serviceId,
            operationId,
            operationParameters
          )
          .pipe(
            map((service) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.CREATE_OPERATION_PARAMETER_SUCCESS });
              return AutomationActions.addOperationParameterSuccess({ operationParameters: service?.data});
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.CREATE_OPERATION_PARAMETER_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteOperationParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteOperationParameters),
      mergeMap(({ projectId, automationProjectId, serviceId, operationId, idList }) =>
        this.restService
          .deleteParameters(projectId, automationProjectId,serviceId, operationId, idList)
          .pipe(
            map((response) =>
            {
              this.toaster.pop({ type: 'success', text: MessageEnum.DELETE_OPERATION_PARAMETER_SUCCESS });
              return AutomationActions.deleteOperationParametersSuccess({
                idList: response?.data?.readyToDelete ? response?.data?.readyToDelete : idList
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.DELETE_OPERATION_PARAMETER_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateOperationParameter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateOperationParameter),
      mergeMap(({ projectId, automationProjectId, serviceId, operationId,id, changes }) =>
        this.restService
          .updateOperationParameters(
            projectId,
            automationProjectId,
            serviceId,
            operationId,
            id,
            changes
          )
          .pipe(
            map((service) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.UPDATE_OPERATION_PARAMETER_SUCCESS });
              return AutomationActions.updateOperationParametersSuccess({
                operationParameters: service?.data
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.UPDATE_OPERATION_PARAMETER_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );


      
  //operation headers
  loadOperationHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.loadOperationHeader),
      mergeMap(({ projectId, automationProjectId, serviceId,operationId }) =>
        this.restService
          .getAllHeaders(projectId, automationProjectId, serviceId, operationId)
          .pipe(
            map((services) =>
              AutomationActions.loadOperationHeaderSuccess({
                operationHeaders: services?.data
              })
            ),
            catchError((error) =>
              of(AutomationActions.automationFailure({ error: error.message }))
            )
          )
      )
    )
  );

  addOperationHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.addOperationHeader),
      mergeMap(({ projectId, automationProjectId, serviceId, operationId,operationHeaders }) =>
        this.restService
          .addHeader(
            projectId,
            automationProjectId,
            serviceId,
            operationId,
            operationHeaders
          )
          .pipe(
            map((service) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.CREATE_OPERATION_HEADER_SUCCESS });
              return AutomationActions.addOperationHeaderSuccess({ operationHeaders: service?.data});
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.CREATE_OPERATION_HEADER_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteOperationHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.deleteOperationHeader),
      mergeMap(({ projectId, automationProjectId, serviceId,operationId,idList }) =>
        this.restService
          .deleteHeaders(projectId, automationProjectId,serviceId,operationId, idList)
          .pipe(
            map((response) =>
            {
              this.toaster.pop({ type: 'success', text: MessageEnum.DELETE_OPERATION_HEADER_SUCCESS });
              return AutomationActions.deleteOperationHeaderSuccess({
                idList: response?.data?.readyToDelete ? response?.data?.readyToDelete : idList
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.DELETE_OPERATION_HEADER_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  updateOperationHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.updateOperationHeader),
      mergeMap(({ projectId, automationProjectId, serviceId,operationId, id, changes }) =>
        this.restService
          .updateHeader(
            projectId,
            automationProjectId,
            serviceId,
            operationId,
            id,
            changes
          )
          .pipe(
            map((service) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.UPDATE_OPERATION_HEADER_SUCCESS });
              return AutomationActions.updateOperationHeaderSuccess({
                operationHeaders: service?.data
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error?.message ? error?.errorMessage : MessageEnum.UPDATE_OPERATION_HEADER_FAILURE });
              return of(
                AutomationActions.automationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

}
